import React, { useState, useRef, useEffect } from 'react';
import { Container, Card, Button, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaCamera, FaArrowLeft } from 'react-icons/fa';
import jsQR from 'jsqr';
import axios from 'axios';

const QRScanner = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [stream, setStream] = useState(null);
  const scanningRef = useRef(false);

  useEffect(() => {
    return () => {
      stopScanning();
    };
  }, []);

  const startScanning = async () => {
    try {
      setError('');
      
      const constraints = {
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      };
      
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        setStream(mediaStream);
        setScanning(true);
        scanningRef.current = true;
        
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          requestAnimationFrame(tick);
        };
      }
    } catch (err) {
      setError('Camera access denied. Please allow camera permission.');
    }
  };

  const stopScanning = () => {
    scanningRef.current = false;
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    setScanning(false);
    setStream(null);
  };

  const tick = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    if (!scanningRef.current || !video || !canvas) return;

    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      const context = canvas.getContext('2d', { willReadFrequently: true });
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      if (canvas.width > 0 && canvas.height > 0) {
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: 'dontInvert'
        });

        if (code && code.data) {
          stopScanning();
          findMember(code.data);
          return;
        }
      }
    }

    if (scanningRef.current) {
      requestAnimationFrame(tick);
    }
  };

  const findMember = async (qrData) => {
    try {
      setSuccess('Searching for member...');
      
      // Extract member ID from QR data (format: GYM-MEMBER-GYM-20250101-123)
      let memberId = qrData;
      if (qrData.includes('GYM-MEMBER-')) {
        memberId = qrData.replace('GYM-MEMBER-', '');
      }
      
      // Search by memberId field
      const res = await axios.get('/api/gym/members', { params: { search: memberId } });
      
      if (res.data && res.data.length > 0) {
        const member = res.data.find(m => m.memberId === memberId) || res.data[0];
        setSuccess('Member found! Redirecting...');
        setTimeout(() => {
          navigate(`/gym/members/${member.id}`);
        }, 500);
      } else {
        setError('Member not found with this QR code');
        setTimeout(() => {
          setError('');
          if (!scanning) {
            startScanning();
          }
        }, 2000);
      }
    } catch (err) {
      console.error('Error finding member:', err);
      setError('Member not found with this QR code');
      setTimeout(() => {
        setError('');
        if (!scanning) {
          startScanning();
        }
      }, 2000);
    }
  };

  const handleManualInput = async () => {
    const memberId = prompt('Enter Member ID (e.g., GYM-0001):');
    if (memberId) {
      await findMember(memberId);
    }
  };

  return (
    <Container fluid className="py-4">
      <Button variant="outline-secondary" className="mb-3" onClick={() => navigate('/gym/members')}>
        <FaArrowLeft className="me-2" /> Back
      </Button>

      <Card className="shadow-sm">
        <Card.Header className="bg-primary text-white">
          <h5 className="mb-0"><FaCamera className="me-2" /> Scan Member QR Code</h5>
        </Card.Header>
        <Card.Body className="text-center">
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          
          {!scanning ? (
            <div className="py-5">
              <FaCamera size={80} className="text-primary mb-3" />
              <h4>Ready to Scan</h4>
              <p className="text-muted">Click the button below to start scanning</p>
              <Button variant="primary" size="lg" className="me-2" onClick={startScanning}>
                <FaCamera className="me-2" /> Start Camera
              </Button>
              <Button variant="outline-secondary" size="lg" onClick={handleManualInput}>
                Enter Manually
              </Button>
            </div>
          ) : (
            <div>
              <div style={{ maxWidth: '500px', margin: '0 auto', position: 'relative' }}>
                <video 
                  ref={videoRef} 
                  autoPlay 
                  playsInline
                  style={{ width: '100%', borderRadius: '10px', background: '#000' }}
                />
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '250px',
                  height: '250px',
                  border: '3px solid #0d6efd',
                  borderRadius: '10px',
                  pointerEvents: 'none'
                }} />
              </div>
              <canvas ref={canvasRef} style={{ display: 'none' }} />
              <div className="mt-3">
                <p className="text-muted">Position the QR code within the blue frame</p>
                <Button variant="danger" onClick={stopScanning}>Stop Scanning</Button>
                <Button variant="outline-secondary" className="ms-2" onClick={handleManualInput}>Enter Manually</Button>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default QRScanner;
